import { useEffect, useMemo, useRef, useState } from 'react';
import { PaneSwitch, type Pane } from '@/components/pane-switch';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type GenerateCustomerQuoteResponse,
  type VendorQuote,
  type ClientQuote,
  type CommitCustomerQuoteResponse,
  type PatchCustomerQuoteResponse,
  type SendCustomerQuoteEmailResponse,
} from '@/lib/api';
import { computePriceBreakdown } from '@shared/markup';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  rfqLineItemId: string;        // public ID
  rfqId?: string;               // public ID of the parent RFQ (for query invalidation)
  vendorQuote: VendorQuote;     // the chosen vendor quote
  lineQty: number;              // customer's requested qty on the line (quote default)
  defaultMarkupPct?: number;    // tenant default for the form's initial value
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

/** Pull core terms out of legacy notes text. Vendor quotes parsed before the
 *  structured columns shipped (2026-08-25) carry the core only as prose, e.g.
 *  "Cost method: Standard Exchange. Core charge: $179,000.00. Core due:
 *  2026-09-09." — this prefills the form so the broker confirms rather than
 *  re-keys. Deterministic regex, no AI. */
export function coreFromNotes(notes: string | null | undefined): {
  isExchange: boolean;
  coreCents: number | null;
  coreDueDate: string | null;
} {
  if (!notes) return { isExchange: false, coreCents: null, coreDueDate: null };
  const charge = notes.match(/core\s*charge\W{0,4}([\d,]+(?:\.\d{1,2})?)/i);
  const due = notes.match(/core\s*due\W{0,4}(\d{4}-\d{2}-\d{2})/i);
  const exch = /exchange|core\s*charge/i.test(notes);
  return {
    isExchange: exch,
    coreCents: charge?.[1] ? Math.round(parseFloat(charge[1].replace(/,/g, '')) * 100) : null,
    coreDueDate: due?.[1] ?? null,
  };
}

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// APD sells FCA/EXW (buyer pays freight). Any other vendor Incoterm means the
// vendor is arranging/including carriage — their price may bundle freight that
// quietly eats margin (the Knight deal lost $220 this way). Flag those.
const APD_SAFE_INCOTERMS = ['EXW', 'FCA'];

// Default the customer validity to the vendor's remaining validity when they
// gave one ("match the vendor's validity"), else 30 days. Server caps anyway.
function defaultValidityFor(validUntil: string | null): number {
  if (!validUntil) return 30;
  const days = Math.ceil((new Date(validUntil).getTime() - Date.now()) / 86_400_000);
  return Math.max(1, days);
}

export function CustomerQuoteModal({ open, onClose, rfqLineItemId, rfqId, vendorQuote, lineQty, defaultMarkupPct = 10 }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [markupPct, setMarkupPct] = useState<number>(defaultMarkupPct);
  const [validityDays, setValidityDays] = useState<number>(defaultValidityFor(vendorQuote.validUntil));
  // Quote the customer's requested qty by default — NOT the vendor's stated stock
  // qty (qtyOffered), which is often larger (e.g. vendor has 26, customer wants 1).
  const [qty, setQty] = useState<number>(lineQty);
  const dismiss = useModalDismiss(onClose);
  const [result, setResult] = useState<GenerateCustomerQuoteResponse | null>(null);
  // Once a quote is committed we keep `saved` populated and switch the
  // sidebar from generate-actions to status-actions (mark sent / accepted).
  const [saved, setSaved] = useState<ClientQuote | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Exchange terms. Structured fields (new parses) win; legacy quotes prefill
  // from notes so the $179k core in a prose blob isn't silently dropped.
  const notesCore = useMemo(() => coreFromNotes(vendorQuote.notes), [vendorQuote.notes]);
  const [isExchange, setIsExchange] = useState<boolean>(
    Boolean(vendorQuote.isExchange) || notesCore.isExchange,
  );
  const [coreDollars, setCoreDollars] = useState<string>(() => {
    const cents = vendorQuote.coreChargeCents ?? notesCore.coreCents;
    return cents != null ? (cents / 100).toFixed(2) : '';
  });
  const [coreDueDate, setCoreDueDate] = useState<string>(() =>
    vendorQuote.coreDueAt
      ? new Date(vendorQuote.coreDueAt).toISOString().slice(0, 10)
      : notesCore.coreDueDate ?? '',
  );
  const [coreBilling, setCoreBilling] = useState<'upfront' | 'on_failure'>('upfront');
  const coreCents = useMemo(() => {
    const n = parseFloat(coreDollars.replace(/,/g, ''));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
  }, [coreDollars]);
  /** Payload fragment shared by preview + commit so the two can never drift. */
  const exchangeBody = useMemo(
    () =>
      isExchange && coreCents
        ? {
            isExchange: true,
            coreChargeCents: coreCents,
            ...(coreDueDate ? { coreDueAt: `${coreDueDate}T23:59:59.000Z` } : {}),
            coreBilling,
          }
        : { isExchange: false },
    [isExchange, coreCents, coreDueDate, coreBilling],
  );

  useEffect(() => {
    if (!open) {
      setMarkupPct(defaultMarkupPct);
      setValidityDays(defaultValidityFor(vendorQuote.validUntil));
      setQty(lineQty);
      setResult(null);
      setSaved(null);
      const nc = coreFromNotes(vendorQuote.notes);
      setIsExchange(Boolean(vendorQuote.isExchange) || nc.isExchange);
      const cents = vendorQuote.coreChargeCents ?? nc.coreCents;
      setCoreDollars(cents != null ? (cents / 100).toFixed(2) : '');
      setCoreDueDate(
        vendorQuote.coreDueAt
          ? new Date(vendorQuote.coreDueAt).toISOString().slice(0, 10)
          : nc.coreDueDate ?? '',
      );
      setCoreBilling('upfront');
    }
  }, [open, defaultMarkupPct, vendorQuote, lineQty]);

  // Live preview — uses the shared computePriceBreakdown so client + server
  // rounding never drift. Single source of truth in shared/markup.ts. Uses the
  // editable qty (the customer's ask), not the vendor's stated stock qty.
  const preview = useMemo(
    () => computePriceBreakdown(vendorQuote.unitPriceCents, qty, markupPct),
    [markupPct, qty, vendorQuote.unitPriceCents],
  );

  const generateMutation = useMutation({
    mutationFn: async () => {
      return api<GenerateCustomerQuoteResponse>('/api/customer-quotes/generate', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          rfqLineItemPublicId: rfqLineItemId,
          vendorQuotePublicId: vendorQuote.id,
          markupPctOverride: markupPct,
          validityDays,
          qty,
          ...exchangeBody,
        }),
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setSaved(null); // dropping back to preview state
    },
  });

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['client-quotes'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api<CommitCustomerQuoteResponse>('/api/customer-quotes', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({
          rfqLineItemPublicId: rfqLineItemId,
          vendorQuotePublicId: vendorQuote.id,
          markupPctOverride: markupPct,
          validityDays,
          qty,
          ...exchangeBody,
        }),
      });
    },
    onSuccess: (data) => {
      // Server-rendered HTML at commit time may differ slightly from the
      // preview if the underlying vendor_quote was edited between calls.
      // Re-render the iframe with the committed snapshot so what the broker
      // sees in the modal matches exactly what's in the DB.
      setSaved(data.clientQuote);
      if (data.clientQuote.html) {
        setResult({
          quoteRef: data.clientQuote.ref,
          rfqRef: data.clientQuote.rfqRef,
          generatedAt: data.clientQuote.generatedAt,
          validUntil: data.clientQuote.validUntil,
          currency: data.clientQuote.currency,
          breakdown: data.breakdown,
          markupSource: data.markupSource,
          html: data.clientQuote.html,
        });
      }
      invalidateLists();
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (newStatus: 'sent' | 'accepted' | 'rejected' | 'expired') => {
      if (!saved) throw new Error('No saved quote to update');
      return api<PatchCustomerQuoteResponse>(`/api/customer-quotes/${saved.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: newStatus }),
      });
    },
    onSuccess: (data) => {
      setSaved(data.clientQuote);
      invalidateLists();
    },
  });

  // Sends the quote as the email body via GHL and marks it sent in one call —
  // replacing generate-PDF → Gmail → retype subject → attach → send → come back
  // and mark it sent. Idempotency-Key guards the double-click: a customer
  // receiving the same quote twice looks careless.
  const [emailed, setEmailed] = useState<SendCustomerQuoteEmailResponse | null>(null);
  const sendKeyRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `send-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailMutation = useMutation({
    mutationFn: async () => {
      if (!saved) throw new Error('No saved quote to send');
      return api<SendCustomerQuoteEmailResponse>(
        `/api/customer-quotes/${saved.id}/send-email`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          // Fresh key per modal session, NOT a fixed key per quote. A fixed key
          // would make a deliberate re-send (customer says it never arrived)
          // silently replay the cached response and send nothing. This still
          // collapses the double-click, which is the actual risk.
          idempotencyKey: sendKeyRef.current,
          body: JSON.stringify({ includeAcceptLink: true }),
        },
      );
    },
    onSuccess: (data) => {
      setEmailed(data);
      setEmailError(null);
      setSaved((prev) => (prev ? { ...prev, status: 'sent', sentAt: data.sentAt } : prev));
      invalidateLists();
    },
    onError: (e: unknown) => {
      // api() rejects with a plain ApiError object, not an Error instance, so an
      // `instanceof Error` check never matches and every failure rendered as the
      // generic fallback — hiding the server's actual message. Read `.message`
      // off the object, as the rest of the client does.
      // `||` not `??`: api() yields message '' when a non-gateway error has an
      // empty body (res.statusText is '' over HTTP/2), and the banner renders
      // on truthiness — so nullish-coalescing would show nothing at all.
      setEmailError((e as { message?: string })?.message || 'Send failed');
    },
  });

  // When the result arrives, write the HTML into the iframe so the document
  // carries its own styling rather than inheriting Tailwind.
  //
  // NOTE: this does NOT escape a Content-Security-Policy. An about:blank iframe
  // is same-origin and INHERITS the creating document's policy, as does a
  // window.open('', '_blank') document. A previous version of this comment
  // claimed otherwise. See docs/design/csp-rollout.md — it is why style-src
  // needs 'unsafe-inline' for the whole app.
  useEffect(() => {
    if (!result || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(result.html);
    doc.close();
  }, [result]);

  const downloadHtml = () => {
    if (!result) return;
    const blob = new Blob([result.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.quoteRef}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.html);
    } catch {
      // Fallback: open prompt with the HTML for manual copy
      window.prompt('Copy the HTML manually:', result.html.slice(0, 100) + '…');
    }
  };

  const openInNewTab = () => {
    if (!result) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(result.html);
    win.document.close();
  };

  // Phones show one pane at a time (see pane-switch.tsx). The preview takes over as soon as a
  // document exists or is regenerated, and every open starts on the right pane.
  const [pane, setPane] = useState<Pane>('edit');
  const previewReady = !!result;
  useEffect(() => {
    setPane(previewReady ? 'preview' : 'edit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Generate customer quote</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Apply markup, generate the branded quote document, send to the customer.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <PaneSwitch pane={pane} onChange={setPane} previewReady={previewReady} editLabel={'Edit'} />
        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[320px_1fr]">
          <aside className={`border-b lg:border-r border-border lg:overflow-auto ${pane === 'edit' ? '' : 'hidden lg:block'} p-5 space-y-5 bg-muted/20`}>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Vendor quote
              </div>
              <div className="text-sm font-medium">{vendorQuote.vendorNameRaw}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {vendorQuote.conditionOffered ?? '—'}
                {vendorQuote.certOffered ? ` · ${vendorQuote.certOffered.replace(/_/g, ' ')}` : ''}
                {vendorQuote.leadTimeDays != null ? ` · ${vendorQuote.leadTimeDays}d lead` : ''}
              </div>
              <div className="mt-2 font-mono text-sm">
                {fmtMoney(vendorQuote.unitPriceCents, vendorQuote.currency)} <span className="text-muted-foreground">/ ea · {vendorQuote.qtyOffered} in stock</span>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Quantity to quote
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                    className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                  <span className="text-sm text-muted-foreground">{vendorQuote.qtyOffered != null ? `EA` : ''}</span>
                  {qty !== lineQty && (
                    <button
                      type="button"
                      onClick={() => setQty(lineQty)}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      reset to requested ({lineQty})
                    </button>
                  )}
                </div>
              </label>
              <label className="block">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Markup % (this quote)
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.5"
                    min={-100}
                    max={1000}
                    value={markupPct}
                    onChange={(e) => setMarkupPct(Number(e.target.value))}
                    className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <button
                    type="button"
                    onClick={() => setMarkupPct(defaultMarkupPct)}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    reset to default ({defaultMarkupPct}%)
                  </button>
                </div>
              </label>
              <label className="block">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Validity (days)
                </div>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={validityDays}
                  onChange={(e) => setValidityDays(Number(e.target.value))}
                  className="w-24 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                />
                {vendorQuote.validUntil ? (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Vendor quote valid until {new Date(vendorQuote.validUntil).toLocaleDateString()} — matched by default; can't exceed it.
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Vendor gave no validity — set a short window if their price is &ldquo;subject to prior sale.&rdquo;
                  </div>
                )}
              </label>

              <div className="block border border-border rounded p-2.5 bg-background/60">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isExchange}
                    onChange={(e) => setIsExchange(e.target.checked)}
                  />
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Exchange (core return required)
                  </span>
                </label>
                {isExchange && (
                  <div className="mt-2 space-y-2">
                    {vendorQuote.coreChargeCents == null && notesCore.coreCents != null && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400">
                        Prefilled from the vendor&rsquo;s notes — confirm before quoting.
                      </div>
                    )}
                    <label className="block">
                      <div className="text-[11px] text-muted-foreground mb-0.5">
                        Core charge ({vendorQuote.currency}) — vendor&rsquo;s figure, never marked up
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={coreDollars}
                        onChange={(e) => setCoreDollars(e.target.value)}
                        placeholder="e.g. 179000.00"
                        className="w-36 px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                      />
                    </label>
                    <label className="block">
                      <div className="text-[11px] text-muted-foreground mb-0.5">Core due back by</div>
                      <input
                        type="date"
                        value={coreDueDate}
                        onChange={(e) => setCoreDueDate(e.target.value)}
                        className="px-2 py-1.5 text-sm bg-background border border-border rounded"
                      />
                    </label>
                    <div className="flex items-center gap-3 text-xs">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="coreBilling"
                          checked={coreBilling === 'upfront'}
                          onChange={() => setCoreBilling('upfront')}
                        />
                        Deposit upfront, refunded on return
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="coreBilling"
                          checked={coreBilling === 'on_failure'}
                          onChange={() => setCoreBilling('on_failure')}
                        />
                        Invoiced only if not returned
                      </label>
                    </div>
                    {!coreDueDate && vendorQuote.coreReturnDays != null && (
                      <div className="text-[11px] text-muted-foreground">
                        No date set — the quote will say &ldquo;within {vendorQuote.coreReturnDays} days of receipt&rdquo;, as the vendor quoted.
                      </div>
                    )}
                    {isExchange && !coreCents && (
                      <div className="text-[11px] text-destructive">
                        Enter the core charge, or untick Exchange to quote it outright.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="border border-border rounded p-3 bg-background space-y-1.5 text-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Pricing preview · {vendorQuote.currency}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vendor unit</span>
                <span className="font-mono">{fmtMoney(vendorQuote.unitPriceCents, vendorQuote.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer unit</span>
                <span className="font-mono">{fmtMoney(preview.customerUnitCents, vendorQuote.currency)}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
                <span className="font-medium">Customer total ({vendorQuote.qtyOffered} ea)</span>
                <span className="font-mono font-semibold text-primary">{fmtMoney(preview.customerTotalCents, vendorQuote.currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-1">
                <span>Margin</span>
                <span className="font-mono">
                  {fmtMoney(preview.marginCents, vendorQuote.currency)} ({preview.marginPct.toFixed(1)}%)
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground pt-1 italic">
                Customer unit price rounded to nearest 5 per APD SOP.
              </div>
            </div>

            {/* Pre-send guards — qty vs stock + expired vendor + margin + shipping */}
            {qty > vendorQuote.qtyOffered && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Quoting {qty}, vendor offered {vendorQuote.qtyOffered}.</strong> Confirm they can supply the full quantity at this price before sending.
              </div>
            )}
            {vendorQuote.validUntil && new Date(vendorQuote.validUntil).getTime() < Date.now() && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                <strong>Vendor quote expired {new Date(vendorQuote.validUntil).toLocaleDateString()}.</strong> Refresh the vendor price first — quoting off it would generate an already-expired customer quote (validity can't exceed the vendor's).
              </div>
            )}
            {preview.marginCents <= 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                <strong>No margin.</strong> This quote sells at {preview.marginCents < 0 ? 'a loss' : 'cost'} ({fmtMoney(preview.marginCents, vendorQuote.currency)}). Raise the markup before sending.
              </div>
            )}
            {preview.marginCents > 0 && preview.marginPct < 5 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Low margin</strong> ({preview.marginPct.toFixed(1)}%). Double-check this is intentional.
              </div>
            )}
            {vendorQuote.incoterms && !APD_SAFE_INCOTERMS.includes(vendorQuote.incoterms) && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>Vendor shipping terms: {vendorQuote.incoterms}{vendorQuote.fobLocation ? ` ${vendorQuote.fobLocation}` : ''}.</strong> Their price likely includes freight/carriage, which can eat your margin. APD always sells FCA (buyer pays) — confirm the vendor isn't bundling shipping into this price before you quote.
              </div>
            )}

            {!result ? (
              <button
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending || (isExchange && !coreCents)}
                className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {generateMutation.isPending ? 'Generating…' : 'Generate preview'}
              </button>
            ) : !saved ? (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Preview
                  </div>
                  <div className="text-sm font-mono">{result.quoteRef}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Markup source: <span className="font-medium">{result.markupSource}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 italic">
                    Not saved yet. Click below to commit.
                  </div>
                </div>
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save quote (draft)'}
                </button>
                <button
                  onClick={downloadHtml}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Download preview .html
                </button>
                <button
                  onClick={copyHtml}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Copy preview HTML
                </button>
                <button
                  onClick={openInNewTab}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Open in new tab
                </button>
                <button
                  onClick={() => { setResult(null); }}
                  className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Adjust markup &amp; regenerate
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Saved · <span className="font-semibold text-primary">{saved.status}</span>
                  </div>
                  <div className="text-sm font-mono">{saved.ref}</div>
                  <div className="text-xs text-muted-foreground mt-1">{saved.id}</div>
                  {saved.sentAt && (
                    <div className="text-[11px] text-muted-foreground mt-1.5">
                      Sent {new Date(saved.sentAt).toLocaleString()}
                    </div>
                  )}
                  {saved.acceptedAt && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Accepted {new Date(saved.acceptedAt).toLocaleString()}
                    </div>
                  )}
                </div>

                {emailed && (
                  <div className="border border-emerald-600/40 bg-emerald-600/10 rounded p-3 space-y-1">
                    <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                      Emailed to {emailed.to}
                    </div>
                    <div className="text-[11px] text-muted-foreground break-words">{emailed.subject}</div>
                    {emailed.documentationPending && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400 pt-1">
                        No cert/paperwork from the vendor — the quote says we are confirming it.
                        Chase the vendor before a PO.
                      </div>
                    )}
                  </div>
                )}
                {emailError && (
                  <div className="border border-destructive/40 bg-destructive/10 rounded p-3 text-xs text-destructive">
                    {emailError}
                  </div>
                )}

                {!emailed && (saved.status === 'draft' || saved.status === 'sent') && (
                  <button
                    onClick={() => emailMutation.mutate()}
                    disabled={emailMutation.isPending}
                    className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                  >
                    {emailMutation.isPending ? 'Sending…' : 'Email quote to customer'}
                  </button>
                )}

                {saved.status === 'draft' && (
                  <button
                    onClick={() => patchMutation.mutate('sent')}
                    disabled={patchMutation.isPending}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
                  >
                    {patchMutation.isPending ? 'Updating…' : 'Mark as sent (no email)'}
                  </button>
                )}
                {saved.status === 'sent' && (
                  <>
                    <button
                      onClick={() => patchMutation.mutate('accepted')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                    >
                      {patchMutation.isPending ? 'Updating…' : 'Mark as accepted'}
                    </button>
                    <button
                      onClick={() => patchMutation.mutate('rejected')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Mark as rejected
                    </button>
                    <button
                      onClick={() => patchMutation.mutate('expired')}
                      disabled={patchMutation.isPending}
                      className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Mark as expired
                    </button>
                  </>
                )}

                <div className="border-t border-border pt-2 mt-2 space-y-2">
                  <button
                    onClick={downloadHtml}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                  >
                    Download .html
                  </button>
                  <button
                    onClick={copyHtml}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                  >
                    Copy HTML to clipboard
                  </button>
                  <button
                    onClick={openInNewTab}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                  >
                    Open in new tab
                  </button>
                </div>
              </div>
            )}

            {generateMutation.isError && (
              <div className="text-sm text-destructive">
                {(generateMutation.error as { message?: string })?.message ?? 'Generation failed'}
              </div>
            )}
            {saveMutation.isError && (
              <div className="text-sm text-destructive">
                {(saveMutation.error as { message?: string })?.message ?? 'Save failed'}
              </div>
            )}
            {patchMutation.isError && (
              <div className="text-sm text-destructive">
                {(patchMutation.error as { message?: string })?.message ?? 'Status update failed'}
              </div>
            )}
          </aside>

          <section className={`overflow-hidden bg-[#f4f6fa] items-stretch min-h-[55dvh] lg:min-h-0 ${pane === 'preview' ? 'flex' : 'hidden lg:flex'}`}>
            {result ? (
              <iframe
                ref={iframeRef}
                title="Quote preview"
                className="w-full h-full border-0 bg-white"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                Adjust markup on the left and click <span className="mx-1 font-medium">Generate quote</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
